import { useMemo } from 'react';
import useSWR, { SWRConfiguration, mutate } from 'swr';
import fetchDataService from '../services/fetchDataService';
import { useConfig } from '../contexts/ConfigContext';
import useAuth from './useAuth';
import {
  JournalEntry,
  AccountItem,
  AccountingConfigItem,
  AccountingPeriodLock,
  TreasuryAccountItem,
  TreasuryMovementItem,
  PartnerBalanceSummary,
} from '../services/accountingService';

/**
 * Helper to safely extract array collections from various response shapes.
 */
export const getArrayData = (data: any, key: string): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data[key])) return data[key];
  if (Array.isArray(data.data)) return data.data;
  if (data.data && Array.isArray(data.data[key])) return data.data[key];
  return [];
};

/**
 * Hook for fetching journal entries with date range and company filtering.
 */
export function useJournalEntries(
  params: {
    company_id?: number;
    start_date?: string;
    end_date?: string;
    all?: boolean;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;
  const startDate = params.start_date;
  const endDate = params.end_date;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/journal-entries', companyId, startDate, endDate, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/journal-entries', activeKey, {
      company_id: companyId,
      start_date: startDate,
      end_date: endDate,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: true,
    ...options,
  });

  const entries: JournalEntry[] = getArrayData(data, 'entries');

  return {
    entries,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching operational movements that are pending or failed in accounting.
 */
export function usePendingAccountingMovements(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/pending-movements', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/pending-movements', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: true,
    ...options,
  });

  const pendingMovements: any[] = getArrayData(data, 'movements');

  return {
    pendingMovements,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching chart of accounts (PUC).
 */
export function useAccounts(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/accounts', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/accounts', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const accounts: AccountItem[] = getArrayData(data, 'accounts');

  return {
    accounts,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching company accounting period lock.
 */
export function usePeriodLock(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/period-lock', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/period-lock', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const rawLock = data?.lock || data?.data?.lock || data?.data || data;
  const lock: AccountingPeriodLock | null = rawLock?.lock_date ? rawLock : null;
  const lockDate: string | null = lock?.lock_date ? String(lock.lock_date).substring(0, 10) : null;

  return {
    lock,
    lockDate,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching contacts/third-parties.
 */
export function useContacts(
  params: {
    company_id?: number;
    company_uid?: string | null;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/contacts/get-contacts', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/contacts/get-contacts', activeKey, {
      company_id: companyId,
      company: params.company_uid || companyId,
      all: true,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

    const rawContacts: any[] = getArrayData(data, 'contactList');
  const contacts = useMemo(() => {
    const seen = new Set<any>();
    return rawContacts.filter((c: any) => {
      if (!c) return false;
      const key = c.id !== undefined && c.id !== null ? c.id : c.uid;
      if (key === undefined || key === null) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawContacts]);

  return {
    contacts,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching accounting configuration mappings.
 */
export function useAccountingConfigs(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/configs', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/configs', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const configs: AccountingConfigItem[] = getArrayData(data, 'configs');

  return {
    configs,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Global mutation helpers to trigger background revalidations anywhere in the application.
 */
export const mutateJournalEntries = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/journal-entries' && (!companyId || key[1] === companyId));

export const mutatePendingAccountingMovements = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/pending-movements' && (!companyId || key[1] === companyId));

export const mutateAccounts = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/accounts' && (!companyId || key[1] === companyId));

export const mutatePeriodLock = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/period-lock' && (!companyId || key[1] === companyId));

export const mutateAccountingConfigs = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/configs' && (!companyId || key[1] === companyId));


export const mutateContacts = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/contacts/get-contacts' && (!companyId || key[1] === companyId));


/**
 * Hook for fetching treasury accounts with balances.
 */
export function useTreasuryAccounts(
  params: {
    company_id?: number;
    company_uid?: string | null;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/treasury-accounts/get-accounts', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/treasury-accounts/get-accounts', activeKey, {
      company_id: companyId,
      company: params.company_uid || companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 10000,
    revalidateOnFocus: true,
    ...options,
  });

  const accounts: TreasuryAccountItem[] = getArrayData(data, 'accounts');

  return {
    accounts,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching treasury movements history.
 */
export function useTreasuryMovements(
  params: {
    company_id?: number;
    company_uid?: string | null;
    start_date?: string;
    end_date?: string;
    movement_type?: string;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/treasury-movements/get-movements', companyId, params.start_date, params.end_date, params.movement_type, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/treasury-movements/get-movements', activeKey, {
      company_id: companyId,
      company: params.company_uid || companyId,
      start_date: params.start_date,
      end_date: params.end_date,
      movement_type: params.movement_type,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 10000,
    revalidateOnFocus: true,
    ...options,
  });

  const movements: TreasuryMovementItem[] = getArrayData(data, 'movements');

  return {
    movements,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching partner balances summary (2355 vs 1325).
 */
export function usePartnerBalances(
  params: {
    company_id?: number;
    company_uid?: string | null;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/reports/partner-balances', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/reports/partner-balances', activeKey, {
      company_id: companyId,
      company: params.company_uid || companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 10000,
    revalidateOnFocus: true,
    ...options,
  });

  const rawBalances = getArrayData(data, 'partnerBalances');
  const partnerBalances: PartnerBalanceSummary[] = rawBalances.length > 0
    ? rawBalances
    : getArrayData(data, 'partners');

  return {
    partnerBalances,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

export const mutateTreasuryAccounts = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/treasury-accounts/get-accounts' && (!companyId || key[1] === companyId));

export const mutateTreasuryMovements = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/treasury-movements/get-movements' && (!companyId || key[1] === companyId));

export const mutatePartnerBalances = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/reports/partner-balances' && (!companyId || key[1] === companyId));
